import axios from "axios";
import config from "@config/backend";
import { getRefreshTokenCookie, setAccessTokenCookie, setRefreshTokenCookie } from "@utils/access-token-cookie";
import { CLIENT_HEADERS } from "@config/client";

interface RefreshResponseVM {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshTokenExpiresIn: number;
}

// Single-flight refresh coordinator.
//
// When the shared access token expires while several requests are in flight, they
// all get a 401 at once — and each axios instance runs its own response
// interceptor, so each would fire its own /refresh. That burst must trigger
// exactly one /refresh call: the backend rotates the refresh token on every call,
// so racing refreshes would invalidate each other. The first caller starts the
// refresh; concurrent callers await the same in-flight promise, then retry.
//
// It deliberately uses a bare axios call — not an HttpGateway — so it does not go
// through the response interceptor (no recursion) and stays independent from the
// Redux dependency graph, letting the interceptor reach it as a module singleton.
class RefreshCoordinator {
  private inflight: Promise<void> | null = null;

  refresh(): Promise<void> {
    if (this.inflight) return this.inflight;
    this.inflight = this.doRefresh().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async doRefresh(): Promise<void> {
    const refreshToken = getRefreshTokenCookie();
    if (!refreshToken) throw new Error("No refresh token to refresh with");

    // Bare axios call, so the client headers the gateways add have to be set here too.
    const { data } = await axios.post<RefreshResponseVM>(
      `${config.url}/v1/public/refresh`,
      { refreshToken },
      { headers: { ...CLIENT_HEADERS } },
    );

    // Persist the rotated pair. setAccessTokenCookie also propagates the new
    // bearer token to every registered gateway (HttpGateways.setBearerToken).
    setAccessTokenCookie(data.accessToken, data.expiresIn);
    setRefreshTokenCookie(data.refreshToken, data.refreshTokenExpiresIn);
  }
}

export const refreshCoordinator = new RefreshCoordinator();
